import { Box, Divider } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useMemo } from 'react';
import type { DataType } from '@/shared/hooks';
import { isLoading } from '@/shared/lib';
import type { MetricDataFormatType } from '@/shared/types';
import { toFormatType } from '@/shared/types';
import { Button as SharedButton, Loader } from '@/shared/ui';
import { isMetricFilter } from '../../model/predicates';
import type { FilterValue } from '../../model/types';
import { useFiltersContext } from '../FiltersContext/FiltersContext';
import { useFiltersParamsContext } from '../FiltersParamsContext/FiltersParamsContext';
import type { Value } from '../NumberFilter/NumberFilter';
import { NumberFilter } from '../NumberFilter/NumberFilter';

const Button = styled(SharedButton)(({ theme }) => ({
  padding: `${theme.spacing(1)} ${theme.spacing(1.5)}`,
  lineHeight: '1.125rem',
  borderRadius: theme.shape.borderRadius * 2,
}));

interface Props {
  id: string;
  onClose: () => void;
}

export function MetricFilterEditor({ id, onClose }: Props) {
  const { filtersMap, updateFilters, submitFilters, resetToInitialFilters } =
    useFiltersContext();
  const { paramsData, metricConfigs } = useFiltersParamsContext();
  const filterConfig = useMemo(
    () => metricConfigs.find((config) => config.id === id),
    [metricConfigs, id],
  );

  if (!filterConfig) {
    return null;
  }

  if (!isMetricFilter(filterConfig.type)) {
    return <p>Unknown filter type: {filterConfig.type}</p>;
  }

  const loading =
    !paramsData[filterConfig.id] ||
    isLoading(paramsData[filterConfig.id].status);

  const handleChange = (id: string) => (value?: FilterValue) => {
    updateFilters(id, value);
  };

  const handleApply = () => {
    submitFilters();
    onClose();
  };
  const handleCancel = () => {
    resetToInitialFilters();
    onClose();
  };

  return (
    <Box sx={{ width: 304 }}>
      <NumberFilter
        sx={{
          p: 2,
          gap: 2,
        }}
        id={filterConfig.id}
        type={filterConfig.type as DataType}
        formatType={toFormatType(filterConfig.dataType as MetricDataFormatType)}
        value={filtersMap[id] as Value}
        onChange={handleChange(id)}
      />
      <Divider />
      <Box sx={{ p: 2, display: 'flex', gap: 2 }}>
        <Button onClick={handleApply}>Apply</Button>
        <Button color="secondary" variant="outlined" onClick={handleCancel}>
          Cancel
        </Button>
      </Box>
      <Loader active={loading} />
    </Box>
  );
}
